import { useEffect, useRef, useState, useCallback, useMemo } from "react";
import { testIds } from "@/testIds";
import { NodeViewWrapper, type NodeViewProps } from "@tiptap/react";
import { renderLatexToElement } from "@/lib/katex-config";
import { useMathMacrosVersion } from "@/lib/useMathMacros";
import { BlockEditModal } from "../BlockEditModal";
import { FormulaCalculator } from "@/components/formula-editor/FormulaCalculator";
import { useEditorStore } from "@/store/editorStore";
import { useSyncStatusStore } from "@/store/syncStatusStore";
import { log as devlog } from "@/components/math-editor/logger";
import { getEquationDisplayNumber } from "@/lib/equation-number";
import styles from "../blocks/blocks.module.scss";

type EquationMode =
  | "inline" | "display" | "display*"
  | "align" | "align*" | "gather" | "gather*" | "multline"
  | "cases" | "split"
  | "pmatrix" | "bmatrix" | "vmatrix" | "Vmatrix";

const VALID_EQUATION_MODES: EquationMode[] = [
  "inline", "display", "display*",
  "align", "align*", "gather", "gather*", "multline",
  "cases", "split",
  "pmatrix", "bmatrix", "vmatrix", "Vmatrix",
];

const MODE_GROUPS: { label: string; modes: EquationMode[] }[] = [
  { label: "Basic", modes: ["inline", "display", "display*"] },
  { label: "Multi-line", modes: ["align", "align*", "gather", "gather*", "multline"] },
  { label: "Structural", modes: ["cases", "split"] },
  { label: "Matrices", modes: ["pmatrix", "bmatrix", "vmatrix", "Vmatrix"] },
];

const MODE_LABELS: Record<EquationMode, string> = {
  inline: "Inline",
  display: "Display",
  "display*": "Display*",
  align: "Align",
  "align*": "Align*",
  gather: "Gather",
  "gather*": "Gather*",
  multline: "Multline",
  cases: "Cases",
  split: "Split",
  pmatrix: "( ) Matrix",
  bmatrix: "[ ] Matrix",
  vmatrix: "| | Matrix",
  Vmatrix: "|| || Matrix",
};

function getWrappedLatex(src: string, mode: EquationMode) {
  switch (mode) {
    case "align":
    case "align*":
    case "split":
      return `\\begin{aligned}${src}\\end{aligned}`;
    case "gather":
    case "gather*":
    case "multline":
      return `\\begin{gathered}${src}\\end{gathered}`;
    case "cases":
      return `\\begin{cases}${src}\\end{cases}`;
    case "pmatrix":
      return `\\begin{pmatrix}${src}\\end{pmatrix}`;
    case "bmatrix":
      return `\\begin{bmatrix}${src}\\end{bmatrix}`;
    case "vmatrix":
      return `\\begin{vmatrix}${src}\\end{vmatrix}`;
    case "Vmatrix":
      return `\\begin{Vmatrix}${src}\\end{Vmatrix}`;
    default:
      return src;
  }
}

export function EquationNodeView({ node, updateAttributes, selected, deleteNode }: NodeViewProps) {
  const previewRef = useRef<HTMLDivElement>(null);

  const latex = (node.attrs.latex as string) || "";
  const label = (node.attrs.label as string) || "";
  const numbered = node.attrs.numbered !== false;
  const rawMode = node.attrs.equationMode;
  const equationMode: EquationMode =
    typeof rawMode === "string" && VALID_EQUATION_MODES.includes(rawMode as EquationMode)
      ? (rawMode as EquationMode)
      : "display";
  const displayMode = equationMode !== "inline";
  const blockId = (node.attrs.blockId as string) || (node.attrs.id as string) || "";
  const docBlocks = useEditorStore((s) => s.blocks);
  const displayNumber = useMemo(
    () => getEquationDisplayNumber(docBlocks, blockId),
    [docBlocks, blockId],
  );

  const [isOpen, setIsOpen] = useState(false);
  const [draftLatex, setDraftLatex] = useState(latex);
  const [draftMode, setDraftMode] = useState<EquationMode>(equationMode);
  const [draftLabel, setDraftLabel] = useState(label);
  const [draftNumbered, setDraftNumbered] = useState(numbered);

  // Drawn again when the document's macros arrive or change (one set with the PDF, 18 Oct).
  const macrosVersion = useMathMacrosVersion();
  useEffect(() => {
    if (!previewRef.current) return;
    if (!latex) {
      previewRef.current.innerHTML = "";
      return;
    }
    const el = previewRef.current;
    const render = () => {
      renderLatexToElement(getWrappedLatex(latex, equationMode), el, { displayMode });
    };
    if (typeof window.requestIdleCallback === "function") {
      const id = window.requestIdleCallback(render, { timeout: 200 });
      return () => window.cancelIdleCallback(id);
    }
    render();
  }, [latex, displayMode, equationMode, macrosVersion]);

  const handleOpen = useCallback(() => {
    setDraftLatex(latex);
    setDraftMode(equationMode);
    setDraftLabel(label);
    setDraftNumbered(numbered);
    setIsOpen(true);
  }, [latex, equationMode, label, numbered]);

  const handleSave = useCallback(() => {
    updateAttributes({ latex: draftLatex, equationMode: draftMode, label: draftLabel, numbered: draftNumbered });
    setIsOpen(false);
  }, [draftLatex, draftMode, draftLabel, draftNumbered, updateAttributes]);

  /**
   * Server-save hook handed down to the MathEditor when this node view
   * hosts it from Flow. Same shape as EquationBlock.handleSaveToServer
   * (commit f3d766f): commit the latex via updateAttributes, mark the
   * editor store dirty, and resolve when the sync store reaches
   * upToDate. 409s recover transparently via the sync rebase loop.
   */
  const handleSaveToServer = useCallback(async (nextLatex: string) => {
    const blockId = (node.attrs.id as string | undefined) ?? "(no-id)";
    devlog.info("save", "EquationNodeView.handleSaveToServer invoked", {
      blockId, latex: nextLatex,
    });
    updateAttributes({ latex: nextLatex });
    useEditorStore.setState((s) => ({
      isDirty: true,
      editVersion: s.editVersion + 1,
    }));
    return new Promise<void>((resolve, reject) => {
      const startedAt = Date.now();
      const timeoutMs = 20000;
      let timer: number | null = null;
      const unsubscribe = useSyncStatusStore.subscribe((s) => {
        if (s.state === "upToDate") {
          if (timer != null) window.clearTimeout(timer);
          unsubscribe();
          devlog.info("save", "EquationNodeView save settled", {
            blockId, roundTripMs: Date.now() - startedAt, serverVersion: s.version,
          });
          resolve();
        } else if (s.state === "error") {
          if (timer != null) window.clearTimeout(timer);
          unsubscribe();
          devlog.error("save", "EquationNodeView save failed", { blockId, detail: s.detail });
          reject(new Error(s.detail ?? "Document save failed"));
        }
      });
      timer = window.setTimeout(() => {
        unsubscribe();
        devlog.warn("save", "EquationNodeView save timeout (still pending)", { blockId, timeoutMs });
        reject(new Error("Save timed out — the document is still pending."));
      }, timeoutMs);
    });
  }, [node.attrs.id, updateAttributes]);

  return (
    <NodeViewWrapper data-type="equation-block" data-testid={testIds.equation.block} data-numbered={numbered ? "true" : "false"} data-block-id={node.attrs.blockId} className={`${styles.equation} ${selected ? "ProseMirror-selectednode" : ""}`}>
      {latex ? (
        <div
          data-testid={testIds.equation.preview}
          className={`${styles.equationPreview} lilia-math-semantic`}
          onClick={handleOpen}
          ref={previewRef}
          title="Click to edit equation"
        />
      ) : (
        <div
          data-testid={testIds.equation.preview}
          className={styles.equationPreview}
          onClick={handleOpen}
          title="Click to add equation"
        >
          <span className={styles.previewLabel}>Click to add equation</span>
        </div>
      )}
      {displayNumber != null && (
        <span data-testid={testIds.equation.number} className={styles.equationLabel} title={label || undefined}>
          ({displayNumber})
        </span>
      )}

      <BlockEditModal
        sourceNode={node}
        title="Edit Equation"
        open={isOpen}
        onClose={() => setIsOpen(false)}
        onSave={handleSave}
        onDelete={() => { setIsOpen(false); deleteNode(); }}
        width={800}
      >
        {/* One editor: settings bar + the shared FormulaCalculator. */}
        <div className="flex flex-col gap-2 flex-1 min-h-0">
          {/* Compact settings bar */}
          <div className="flex items-center gap-2 flex-wrap">
            <select
              data-testid={testIds.equation.mode}
              value={draftMode}
              onChange={(e) => setDraftMode(e.target.value as EquationMode)}
              className="px-2 py-1.5 rounded-md text-[11px] font-medium border border-[var(--lilia-border,#e2e8f0)] bg-[var(--lilia-bg,#fff)] text-[var(--lilia-text,#334155)] focus:outline-none focus:border-[var(--lilia-primary,#0A76A4)]"
            >
              {MODE_GROUPS.map((group) => (
                <optgroup key={group.label} label={group.label}>
                  {group.modes.map((mode) => (
                    <option key={mode} value={mode}>{MODE_LABELS[mode]}</option>
                  ))}
                </optgroup>
              ))}
            </select>

            <label className="flex items-center gap-1.5 cursor-pointer select-none">
              <input
                data-testid={testIds.equation.numbered}
                type="checkbox"
                checked={draftNumbered}
                onChange={(e) => setDraftNumbered(e.target.checked)}
                className="w-3.5 h-3.5 rounded border-[var(--lilia-border,#e2e8f0)] accent-[var(--lilia-primary,#0A76A4)]"
              />
              <span className="text-[11px] font-medium text-[var(--lilia-text-muted,#94a3b8)]">Numbered</span>
            </label>

            <div className="flex items-center gap-1.5">
              <label className="text-[11px] font-medium text-[var(--lilia-text-muted,#94a3b8)]">Label</label>
              <input
                data-testid={testIds.equation.label}
                type="text"
                value={draftLabel}
                onChange={(e) => setDraftLabel(e.target.value)}
                placeholder="eq:euler"
                className="w-28 px-2 py-1.5 text-[11px] font-mono border border-[var(--lilia-border,#e2e8f0)] rounded-md bg-[var(--lilia-bg,#fff)] text-[var(--lilia-text,#334155)] focus:outline-none focus:border-[var(--lilia-primary,#0A76A4)]"
              />
            </div>
          </div>

          {/* One shared equation editor — FormulaCalculator (Symbols /
              Structures / Source), identical to EquationBlock. No separate
              Visual/LaTeX toggle or source-mode widgets: the Source tab inside
              FormulaCalculator covers raw LaTeX. */}
          <div
            data-testid="equation-block-visual-mode"
            data-block-id={node.attrs.id ?? ""}
            className="flex-1 min-h-0 flex flex-col"
          >
            <FormulaCalculator
              value={draftLatex}
              onChange={(val) => { setDraftLatex(val); }}
              onClose={() => {}}
              onSaveToServer={handleSaveToServer}
            />
          </div>
        </div>
      </BlockEditModal>
    </NodeViewWrapper>
  );
}
